/**
 * =====================================
 * FRONTEND IMAGE UTILS - MULTI-TENANT
 * =====================================
 * File: frontend/src/utils/imageUtils.js
 * Data: 29 ottobre 2025
 * Descrizione: Helper per gestire URL immagini multi-tenant
 */

import { useState, useEffect } from 'react';

/**
 * Ottiene l'URL completo di un'immagine tenant-specific
 * @param {string} relativePath - Path relativo (es: 'avatars/elderly_1.jpg', 'carousel/image.png')
 * @param {string} customerId - Customer ID (es: '001', '002')
 * @returns {string} URL completo (es: '/uploads/customer_001/avatars/elderly_1.jpg')
 */
export const getImageUrl = (relativePath, customerId = null) => {
    // Se path vuoto/null, ritorna default avatar
    if (!relativePath || relativePath === '' || relativePath === 'null') {
        return '/uploads/shared/default-avatar.jpg';
    }
    
    // Se già URL assoluto (http/https), ritorna così
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
        return relativePath;
    }
    
    // Se già percorso assoluto locale (inizia con /), ritorna così
    if (relativePath.startsWith('/')) {
        return relativePath;
    }
    
    // Se path shared
    if (relativePath.startsWith('shared/')) {
        return `/uploads/${relativePath}`;
    }
    
    // Se customerId non fornito, prova a recuperarlo dal localStorage
    if (!customerId) {
        customerId = getStoredCustomerId();
    }
    
    // Se ancora non abbiamo customerId, usa default
    if (!customerId) {
        console.warn('[ImageUtils] Customer ID non disponibile, uso default 001');
        customerId = '001';
    }
    
    // Costruisci URL tenant-specific
    return `/uploads/customer_${customerId}/${relativePath}`;
};

/**
 * Recupera il Customer ID dal localStorage (salvato al login)
 * @returns {string|null} Customer ID o null se non trovato
 */
export const getStoredCustomerId = () => {
    try {
        // PRIORITÀ 1: Recupera da persistedAuth (localStorage)
        const persistedAuth = localStorage.getItem('persistedAuth');
        if (persistedAuth) {
            const auth = JSON.parse(persistedAuth);
            if (auth && auth.customer_id) {
                return auth.customer_id;
            }
        }
        
        // PRIORITÀ 2: Recupera da sessionAuth (sessionStorage)
        const sessionAuth = sessionStorage.getItem('sessionAuth');
        if (sessionAuth) {
            const auth = JSON.parse(sessionAuth);
            if (auth && auth.customer_id) {
                return auth.customer_id;
            }
        }
        
        // PRIORITÀ 3: Prova anche da customerId diretto
        const customerId = localStorage.getItem('customerId');
        if (customerId) {
            return customerId;
        }
        
        // PRIORITÀ 4: Fallback: prova a estrarre dal token JWT
        const token = localStorage.getItem('token');
        if (token) {
            const payload = parseJwt(token);
            if (payload && payload.UserInfo && payload.UserInfo.customer_id) {
                return payload.UserInfo.customer_id;
            }
        }
        
        return null;
    } catch (error) {
        console.error('[ImageUtils] Errore nel recupero Customer ID:', error);
        return null;
    }
};

/**
 * Decodifica JWT token per estrarre payload
 * @param {string} token - JWT token
 * @returns {object|null} Payload decodificato o null
 */
const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('[ImageUtils] Errore parsing JWT:', error);
        return null;
    }
};

/**
 * React Hook per ottenere il Customer ID corrente
 * Ritorna customerId e funzione per aggiornarlo
 * @returns {[string, function]} [customerId, setCustomerId]
 */
export const useCustomerId = () => {
    const [customerId, setCustomerId] = useState(null);
    
    useEffect(() => {
        // Recupera customerId al mount del componente
        const storedId = getStoredCustomerId();
        if (storedId) {
            setCustomerId(storedId);
        } else {
            // Se non trovato, usa default
            console.warn('[ImageUtils] useCustomerId: nessun customer ID trovato, uso default 001');
            setCustomerId('001');
        }
    }, []);
    
    return [customerId, setCustomerId];
};

/**
 * Converte vecchio path assoluto in path relativo
 * Usato per retrocompatibilità con database legacy
 * @param {string} absolutePath - Path assoluto vecchio (es: './assets/img/avatars/file.jpg')
 * @returns {string} Path relativo (es: 'avatars/file.jpg')
 */
export const convertLegacyPath = (absolutePath) => {
    if (!absolutePath) {
        return 'shared/default-avatar.jpg';
    }
    
    // Già path relativo
    if (!absolutePath.startsWith('./') && !absolutePath.startsWith('/')) {
        return absolutePath;
    }
    
    // Rimuovi prefissi legacy
    let cleanPath = absolutePath;
    
    // ./assets/img/avatars/file.jpg → avatars/file.jpg
    cleanPath = cleanPath.replace(/^\.\/assets\/img\//, '');
    
    // /images/avatars/file.jpg → avatars/file.jpg
    cleanPath = cleanPath.replace(/^\/images\//, '');
    
    // ./assets/img/avatars/default.jpeg → shared/default-avatar.jpg
    if (cleanPath === 'avatars/default.jpeg' || cleanPath === 'default.jpeg') {
        return 'shared/default-avatar.jpg';
    }
    
    return cleanPath;
};

/**
 * Valida se un file è un'immagine supportata
 * @param {File} file - File da validare
 * @returns {boolean} True se è un'immagine valida
 */
export const isValidImageFile = (file) => {
    if (!file) return false;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    // Controlla MIME type
    if (!validTypes.includes(file.type)) {
        return false;
    }
    
    // Controlla estensione
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    return hasValidExtension;
};

/**
 * Ottiene dimensioni ottimali per avatar (thumbnail)
 * @param {number} originalWidth - Larghezza originale
 * @param {number} originalHeight - Altezza originale
 * @param {number} maxSize - Dimensione massima (default: 200px)
 * @returns {object} {width, height} dimensioni ottimizzate
 */
export const getAvatarDimensions = (originalWidth, originalHeight, maxSize = 200) => {
    const ratio = Math.min(maxSize / originalWidth, maxSize / originalHeight);
    return {
        width: Math.round(originalWidth * ratio),
        height: Math.round(originalHeight * ratio)
    };
};

/**
 * Converte File in Data URL per preview immediato
 * @param {File} file - File immagine
 * @returns {Promise<string>} Data URL dell'immagine
 */
export const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

/**
 * Ottiene URL per avatar anziano (helper specifico)
 * @param {object} elderly - Oggetto anziano con campo avatar
 * @returns {string} URL avatar o default
 */
export const getElderlyAvatarUrl = (elderly) => {
    if (!elderly || !elderly.avatar) {
        return '/uploads/shared/default-avatar.jpg';
    }
    return getImageUrl(elderly.avatar);
};

/**
 * Ottiene URL per immagine carousel (helper specifico)
 * @param {object} carouselImage - Oggetto carousel con campo filename
 * @returns {string} URL immagine carousel
 */
export const getCarouselImageUrl = (carouselImage) => {
    if (!carouselImage || !carouselImage.filename) {
        return null;
    }
    return getImageUrl(carouselImage.filename);
};

/**
 * Gestisce errore caricamento immagine con fallback
 * @param {Event} event - Evento onError dell'immagine
 */
export const handleImageError = (event) => {
    console.warn('[ImageUtils] Errore caricamento immagine, uso default');
    event.target.src = '/uploads/shared/default-avatar.jpg';
    event.target.onerror = null; // Previeni loop infinito
};

// Export default per import semplificato
export default {
    getImageUrl,
    getStoredCustomerId,
    useCustomerId,
    convertLegacyPath,
    isValidImageFile,
    getAvatarDimensions,
    fileToDataUrl,
    getElderlyAvatarUrl,
    getCarouselImageUrl,
    handleImageError
};
